/**
 * Subscription Modal Component
 * Displays subscription plans and handles purchase flow
 * Supports both mobile (IAP) and web (Stripe) checkout
 */

import { Capacitor } from '@capacitor/core';
import { useDismissOnSessionChange } from '../hooks/useDismissOnSessionChange';
import { Browser } from '@capacitor/browser';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { IconType } from 'react-icons';
import {
  LuCheck,
  LuCrown,
  LuExternalLink,
  LuLoaderCircle,
  LuRefreshCw,
  LuStar,
  LuX,
} from 'react-icons/lu';
import { toast } from 'react-toastify';
import { SUBSCRIPTION_PRODUCTS } from '../config/subscriptions.config';
import {
  Button,
  IconButton,
  IconMedallion,
  Pill,
  type PillVariant,
} from '../design-system/components';
import { cx } from '../design-system/components/cx';
import { clickGuard } from '../design-system/components/inertControl';
import { useDialogFocus } from '../hooks/useDialogFocus';
import { useDialogLock } from '../hooks/useDialogLock';
import {
  createStripeCheckoutSession,
  fetchPlansFromBackend,
} from '../services/subscription-backend.service';
import { useSubscriptionStore } from '../stores/subscription.store';
import type { SubscriptionPlan } from '../types';
import { fireAndForget } from '../utils/fireAndForget';
import { trackGaEvent } from '../utils/ga';
import { trackUserEvent } from '../utils/reddit-pixel';
import { getShareBaseUrl } from '../utils/share-url';
import { useOpenerRef } from '../hooks/useOpenerRef';

// Real features based on config/plans.php
const PREMIUM_FEATURES = [
  {
    icon: '🎤',
    title: 'Oral Quiz Questions',
    description: 'Practice with voice-based questions for deeper learning',
  },
  {
    icon: '🦒',
    title: 'Giraffe Translator',
    description: 'AI-powered tool to translate language into NVC',
  },
  {
    icon: '🎯',
    title: 'Needs Guesser',
    description: 'AI tool to help identify underlying needs',
  },
  {
    icon: '🤖',
    title: 'Empy AI Tutor',
    description: 'Personal AI tutor for empathy practice',
  },
  {
    icon: '🎧',
    title: 'On-Demand Empathy Sessions',
    description: 'Live video sessions for giving or receiving empathy',
  },
  {
    icon: '🎮',
    title: 'Full Game Access',
    description: 'Unlock all games and premium game features',
  },
  {
    icon: '💰',
    title: 'Empathy Marketplace',
    description: 'Earn tips by offering empathy sessions to others',
  },
];

interface MobileSubscriptionProduct {
  id: string;
  title: string;
  description: string;
  price: string;
  pricing?: {
    price: string;
  };
}

interface MobilePlanLimit {
  value: string;
  label: string;
  multiplier?: string;
}

interface MobilePlanCardProps {
  product: MobileSubscriptionProduct | null;
  isSelected: boolean;
  onSelect: () => void;
  disabled?: boolean;
  /** A request is out (#1060 review 1): the Card keeps its state and refuses. */
  locked?: boolean;
  icon: IconType;
  accentColor: string;
  badge?: string;
  limits: MobilePlanLimit[];
  products: SubscriptionPlan[];
}

// Plan credit limits — must match server/config/plans.php
// If plans change frequently, consider fetching from a backend endpoint instead.
const PLAN_CREDITS: Record<string, number> = {
  premium: 2500,
  elite: 30000,
};

function formatCredits(planId: string): string {
  const credits = PLAN_CREDITS[planId];
  return credits ? credits.toLocaleString() : '0';
}

/** Reusable plan card component for mobile IAP subscription UI */
// On the design-system foundations (redesign PR24): a Card that is the
// control, the price in the display font, the badge on a Pill. The Card
// colours its icon from `accentColor` and `disabled` (the current plan).
const MobilePlanCard = ({
  product,
  isSelected,
  onSelect,
  disabled,
  locked = false,
  icon: Icon,
  accentColor,
  badge,
  limits,
  products,
}: MobilePlanCardProps) => {
  if (!product) return null;

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={clickGuard(locked, () => {
        if (!disabled) onSelect?.();
      })}
      aria-pressed={isSelected}
      aria-disabled={locked || undefined}
      className={cx(
        'ee-card ee-modal-plan',
        accentColor !== 'primary' && 'ee-modal-plan--elite',
        disabled ? 'ee-modal-plan--current' : isSelected && 'ee-modal-plan--selected',
      )}
    >
      {badge ? (
        <span className="ee-modal-plan__badge">
          <Pill variant={disabled ? 'neutral' : accentColor === 'primary' ? 'gold' : 'accent'}>
            {badge}
          </Pill>
        </span>
      ) : null}

      <span className="ee-modal-plan__top">
        <span className="ee-modal-plan__name-row">
          <Icon className="ee-modal-plan__icon" aria-hidden="true" />
          <h3 className="ee-modal-plan__name">{product.title}</h3>
        </span>
        <span className={cx('ee-modal-plan__radio', isSelected && 'ee-modal-plan__radio--on')}>
          {isSelected ? <LuCheck aria-hidden="true" /> : null}
        </span>
      </span>
      <span className="ee-modal-plan__desc">{product.description}</span>
      <span className="ee-modal-plan__price">
        {products.find((p) => p.id === product.id)?.price || product.price}
      </span>

      <span className="ee-modal-plan__features">
        {limits.map((limit) => (
          <span key={limit.label} className="ee-modal-plan__feature">
            <LuCheck className="ee-modal-plan__check" aria-hidden="true" />
            <span>
              <strong>{limit.value}</strong> {limit.label}
              {limit.multiplier ? (
                <span className="ee-modal-plan__multiplier"> ({limit.multiplier})</span>
              ) : null}
            </span>
          </span>
        ))}
      </span>
    </button>
  );
};

interface WebPlan {
  stripe_price_id: string;
  plan_id: string;
  billing_type: string;
  price: string | number;
  name?: string;
  [key: string]: unknown;
}

interface WebPlanCardProps {
  plan: WebPlan;
  /** The plan's name as the Card shows it (Premium, Elite). */
  name: string;
  icon: IconType;
  /** The Elite Card's colouring. */
  elite?: boolean;
  /** The user's current plan: marked, and not a control. */
  current: boolean;
  selected: boolean;
  onClick: () => void;
  /** A request is out (#1060 review 1): the Card keeps its state and refuses. */
  locked: boolean;
  /** The Pill's words, or null for none. */
  badge: string | null;
  badgeVariant: PillVariant;
  /** "month" or "year", after the price. */
  period: string;
  /** The yearly saving in dollars, or null when it is not shown. */
  savings: string | null;
  /** The first feature row. */
  included: string;
  /** The monthly credits, already formatted. */
  credits: string;
  /** Elite's credits multiple over Premium, shown after the credits. */
  multiplier?: string;
}

/**
 * One web (Stripe) plan Card: the Premium and Elite Cards were two copies of
 * this markup (review round 1 of #1047, finding 7). The caller works out
 * every value (the comparison, the badge, the saving); the Card only draws
 * them, so the markup is what each copy rendered.
 */
const WebPlanCard = ({
  plan,
  name,
  icon: Icon,
  elite = false,
  current,
  selected,
  onClick,
  locked,
  badge,
  badgeVariant,
  period,
  savings,
  included,
  credits,
  multiplier,
}: WebPlanCardProps) => (
  <button
    type="button"
    disabled={current}
    aria-pressed={selected}
    aria-disabled={locked || undefined}
    onClick={clickGuard(locked, onClick)}
    className={cx(
      'ee-card ee-modal-plan',
      elite && 'ee-modal-plan--elite',
      current ? 'ee-modal-plan--current' : selected && 'ee-modal-plan--selected',
    )}
  >
    {badge ? (
      <span className="ee-modal-plan__badge">
        <Pill variant={badgeVariant}>{badge}</Pill>
      </span>
    ) : null}

    <span className="ee-modal-plan__top">
      <span className="ee-modal-plan__name-row">
        <Icon className="ee-modal-plan__icon" aria-hidden="true" />
        <h3 className="ee-modal-plan__name">{name}</h3>
      </span>
      <span
        className={cx('ee-modal-plan__radio', (current || selected) && 'ee-modal-plan__radio--on')}
      >
        {current || selected ? <LuCheck aria-hidden="true" /> : null}
      </span>
    </span>

    <span>
      <span className="ee-modal-plan__price-row">
        <span className="ee-modal-plan__price">${plan.price}</span>
        <span className="ee-modal-plan__period">/{period}</span>
      </span>
      {savings != null ? <span className="ee-modal-plan__save">Save ${savings}/year</span> : null}
    </span>

    <span className="ee-modal-plan__features">
      <span className="ee-modal-plan__feature">
        <LuCheck className="ee-modal-plan__check" aria-hidden="true" />
        <span>{included}</span>
      </span>
      <span className="ee-modal-plan__feature">
        <LuCheck className="ee-modal-plan__check" aria-hidden="true" />
        <span>
          <strong>{credits}</strong> credits
          <span className="ee-modal-plan__muted">/mo</span>
          {multiplier ? (
            <>
              {' '}
              <span className="ee-modal-plan__multiplier">({multiplier})</span>
            </>
          ) : null}
        </span>
      </span>
    </span>
  </button>
);

interface SubscriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const SubscriptionModal = ({ isOpen, onClose }: SubscriptionModalProps) => {
  // Opened for one session: a sign-out or a switch closes it (#940 r8).
  useDismissOnSessionChange(isOpen, onClose);
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  const [webPlans, setWebPlans] = useState<WebPlan[]>([]);
  const [webLoading, setWebLoading] = useState(false);
  const [webError, setWebError] = useState<string | null>(null);
  const [isPurchasingWeb, setIsPurchasingWeb] = useState(false);

  const {
    products,
    isPurchasing,
    isRestoring,
    purchase,
    restore,
    clearError,
    planId: currentPlanId,
    subscription: currentSubscription,
  } = useSubscriptionStore();

  const isWeb = !Capacitor.isNativePlatform();
  // Redesign PR24: the app's dialog keyboard contract (useDialogFocus), which
  // this modal did not have. Focus moves into the dialog, Tab stays inside,
  // focus goes back to the opener; Escape is the close button, so it does
  // nothing while that button is disabled: while a request is out (the
  // Stripe checkout and its redirect on the web, the store purchase or
  // restore in the app), as on the group Empathy sheets (#1055, q22).
  // The same flag gates every action (#1060 review 1): the purchase and
  // restore cannot interleave, and the billing toggle and the plan Cards
  // keep what the request is buying.
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const openerRef = useOpenerRef(isOpen);
  const busy = isWeb ? isPurchasingWeb : isPurchasing || isRestoring;
  const { dismiss, closeProps } = useDialogLock(busy, onClose);
  useDialogFocus(isOpen, dialogRef, { onEscape: dismiss, returnFocusTo: openerRef });
  // The purchase-success close, held so it can be called off. The picker
  // stays mounted while closed, so closing it (or unmounting) voids it, and
  // so does a new action: a close scheduled for one session never ends the
  // next one (review round 1 of #1070).
  const successClose = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelSuccessClose = useCallback(() => {
    if (successClose.current !== null) clearTimeout(successClose.current);
    successClose.current = null;
  }, []);
  useEffect(() => cancelSuccessClose, [isOpen, cancelSuccessClose]);

  // Legal links must open reliably on native: a relative href + target=_blank
  // opens a blank view in the iOS WKWebView. Open absolute URLs via the Capacitor
  // Browser plugin (works on web + native). We link our own custom Terms (EULA)
  // at /terms-of-use rather than Apple's standard EULA, since ours is custom.
  const TERMS_URL = 'https://empathease.app/terms-of-use';
  const PRIVACY_URL = 'https://empathease.app/privacy';
  const openLegal = (url: string) => {
    void Browser.open({ url });
  };

  // Normalize billing period - backend uses 'annual', frontend uses 'yearly'
  const normalizeBillingPeriod = (period: string | undefined): string => {
    if (period === 'annual' || period === 'yearly') return 'yearly';
    return period || 'monthly'; // 'monthly', 'lifetime', etc.
  };

  // Helper to check if a plan is the user's current plan (only if actively renewing)
  const isCurrentPlan = useCallback(
    (plan: WebPlan): boolean => {
      if (!currentSubscription || !plan) return false;
      // Don't mark as current if subscription is cancelled - user should be able to renew
      if (currentSubscription.status === 'cancelled') return false;
      return (
        plan.stripe_price_id === currentSubscription.product_id ||
        (plan.plan_id === currentPlanId &&
          normalizeBillingPeriod(plan.billing_type) ===
            normalizeBillingPeriod(currentSubscription.billing_period))
      );
    },
    [currentSubscription, currentPlanId],
  );

  // Helper to determine plan comparison (upgrade, downgrade, or lateral)
  const getPlanComparison = (plan: WebPlan): 'upgrade' | 'downgrade' | 'current' => {
    if (!currentPlanId || currentPlanId === 'free') return 'upgrade';
    if (isCurrentPlan(plan)) return 'current';

    const tierOrder: Record<string, number> = { free: 0, premium: 1, elite: 2 };
    const periodOrder: Record<string, number> = { monthly: 0, yearly: 1 };

    const currentTier = tierOrder[currentPlanId] || 0;
    const newTier = tierOrder[plan.plan_id] || 0;
    const currentPeriod =
      periodOrder[normalizeBillingPeriod(currentSubscription?.billing_period)] || 0;
    const newPeriod = periodOrder[normalizeBillingPeriod(plan.billing_type)] || 0;

    if (newTier > currentTier) return 'upgrade';
    if (newTier < currentTier) return 'downgrade';
    // Same tier, different period
    if (newPeriod > currentPeriod) return 'upgrade';
    if (newPeriod < currentPeriod) return 'downgrade';
    return 'current';
  };

  // Fetch web plans when modal opens on web
  useEffect(() => {
    if (isOpen && isWeb) {
      void fetchWebPlans();
    }
  }, [isOpen, isWeb]);

  // Reddit pixel: signal "viewed pricing" — captures intent earlier than
  // AddToCart, since many users open the modal and bounce before clicking
  // a tier. Logged-in user, so consent-gated via trackUserEvent.
  useEffect(() => {
    if (isOpen) {
      trackUserEvent('ViewContent');
      trackGaEvent('view_pricing');
    }
  }, [isOpen]);

  // Clear error when modal opens
  useEffect(() => {
    if (isOpen) {
      clearError();
      setWebError(null);
    }
  }, [isOpen, clearError]);

  // Set default selected plan when plans load (select first non-current plan)
  useEffect(() => {
    if (isWeb && webPlans.length > 0 && !selectedPlan) {
      // Find a plan that's not the current one
      const availablePlans = webPlans.filter((p) => p.billing_type === billingCycle);
      const nonCurrentPlan = availablePlans.find((p) => !isCurrentPlan(p));
      // Prefer Elite if available, otherwise first available
      const elitePlan = availablePlans.find((p) => p.plan_id === 'elite' && !isCurrentPlan(p));
      const premiumPlan = availablePlans.find((p) => p.plan_id === 'premium' && !isCurrentPlan(p));
      setSelectedPlan(
        elitePlan?.stripe_price_id ||
          premiumPlan?.stripe_price_id ||
          nonCurrentPlan?.stripe_price_id ||
          null,
      );
    }
  }, [webPlans, selectedPlan, isWeb, billingCycle, currentSubscription, isCurrentPlan]);

  // Update selected plan when billing cycle changes
  useEffect(() => {
    if (isWeb && webPlans.length > 0 && selectedPlan) {
      const currentPlan = webPlans.find((p) => p.stripe_price_id === selectedPlan);
      if (currentPlan) {
        const sameTierPlan = webPlans.find(
          (p) => p.plan_id === currentPlan.plan_id && p.billing_type === billingCycle,
        );
        if (sameTierPlan) {
          setSelectedPlan(sameTierPlan.stripe_price_id);
        }
      }
    }
  }, [billingCycle, isWeb, selectedPlan, webPlans]);

  const fetchWebPlans = async (): Promise<void> => {
    setWebLoading(true);
    setWebError(null);
    const result = await fetchPlansFromBackend('web');
    if (result.success) {
      // Type assertion is safe - backend returns plans with all required WebPlan fields for web platform
      setWebPlans(result.data as unknown as WebPlan[]);
    } else {
      setWebError(result.message || 'Failed to load plans');
    }
    setWebLoading(false);
  };

  const handlePurchase = async (): Promise<void> => {
    if (!selectedPlan) return;
    cancelSuccessClose();
    const result = await purchase(selectedPlan);
    if (result.success) {
      // Through the lock as well: dismiss drops the close while busy.
      successClose.current = setTimeout(() => {
        successClose.current = null;
        dismiss();
      }, 1500);
    } else if (result.error) {
      // The picker renders no `error`: say it, as restore does. A cancel
      // carries none and stays silent.
      toast.error(result.error);
    }
  };

  const handleWebPurchase = async (): Promise<void> => {
    if (!selectedPlan) return;

    setIsPurchasingWeb(true);
    setWebError(null);
    const plan = webPlans.find((p) => p.stripe_price_id === selectedPlan);
    const price = plan ? parseFloat(String(plan.price)) : 0;
    if (price > 0) {
      trackUserEvent('AddToCart', { currency: 'USD', value: price, itemCount: 1 });
      trackGaEvent('begin_checkout', { currency: 'USD', value: price });
    }

    const successUrl = `${getShareBaseUrl()}/subscription/success?session_id={CHECKOUT_SESSION_ID}`;
    const cancelUrl = `${getShareBaseUrl()}/subscription/cancel`;

    const result = await createStripeCheckoutSession(selectedPlan, successUrl, cancelUrl);

    if (result.success && result.data?.url) {
      window.location.href = result.data.url;
    } else {
      setWebError(result.message || 'Failed to start checkout');
      setIsPurchasingWeb(false);
    }
  };

  const handleRestore = async (): Promise<void> => {
    cancelSuccessClose();
    const result = await restore();
    if (result.success) {
      toast.success('Purchases restored successfully!');
    } else if (result.error) {
      toast.error(result.error);
    }
  };

  // Calculate yearly savings
  const getYearlySavings = (
    monthlyPrice: number,
    yearlyPrice: number,
  ): { savings: string; percent: number } => {
    const yearlyAtMonthlyRate = monthlyPrice * 12;
    const savings = yearlyAtMonthlyRate - yearlyPrice;
    const percentSaved = Math.round((savings / yearlyAtMonthlyRate) * 100);
    return { savings: savings.toFixed(0), percent: percentSaved };
  };

  if (!isOpen) return null;

  // Filter plans by billing cycle
  const filteredPlans = webPlans.filter((p) => p.billing_type === billingCycle);
  const premiumPlan = filteredPlans.find((p) => p.plan_id === 'premium');
  const elitePlan = filteredPlans.find((p) => p.plan_id === 'elite');

  // Get monthly prices for savings calculation
  const premiumMonthly = webPlans.find(
    (p) => p.plan_id === 'premium' && p.billing_type === 'monthly',
  );
  const eliteMonthly = webPlans.find((p) => p.plan_id === 'elite' && p.billing_type === 'monthly');

  // Web platform with Stripe checkout
  if (isWeb) {
    return (
      <div
        ref={dialogRef}
        tabIndex={-1}
        className="ee-modal fixed inset-0 z-(--ee-z-dialog)"
        role="dialog"
        aria-modal="true"
        aria-labelledby="subscription-modal-title"
      >
        <div className="ee-card ee-modal__dialog ee-modal__dialog--wide">
          {/* Header */}
          <div className="ee-modal__head">
            <div className="ee-modal__heading">
              <IconMedallion icon={LuCrown} size="sm" className="ee-modal__medallion" />
              <div>
                <h2 id="subscription-modal-title" className="ee-modal__title">
                  {currentPlanId === 'free' ? 'Upgrade Your Plan' : 'Change Your Plan'}
                </h2>
                <p className="ee-modal__sub">
                  {currentPlanId === 'free'
                    ? 'Unlock all features'
                    : 'Switch plans or billing cycle'}
                </p>
              </div>
            </div>
            <IconButton label="Close subscription modal" icon={<LuX />} {...closeProps} />
          </div>

          {/* Content */}
          <div className="ee-modal__body">
            {/* Error message */}
            {webError ? (
              <div className="ee-modal-alert ee-modal-alert--danger">
                <p>{webError}</p>
              </div>
            ) : null}

            {/* Loading state */}
            {webLoading ? (
              <div className="ee-modal__loading">
                <LuLoaderCircle
                  className="ee-modal__spinner animate-spin motion-reduce:animate-none"
                  aria-hidden="true"
                />
              </div>
            ) : null}

            {!webLoading && webPlans.length === 0 && !webError && (
              <div className="ee-modal__empty">
                <p className="ee-modal__empty-title">
                  No subscription plans available at this time.
                </p>
                <p className="ee-modal__empty-text">Please try again later or contact support.</p>
              </div>
            )}

            {!webLoading && webPlans.length > 0 && (
              <>
                {/* Billing Cycle Toggle */}
                <div className="ee-modal-seg">
                  <button
                    onClick={clickGuard(busy, () => setBillingCycle('monthly'))}
                    aria-pressed={billingCycle === 'monthly'}
                    aria-disabled={busy || undefined}
                    className={cx(
                      'ee-modal-seg__option',
                      billingCycle === 'monthly' && 'ee-modal-seg__option--on',
                    )}
                  >
                    Monthly
                  </button>
                  <button
                    onClick={clickGuard(busy, () => setBillingCycle('yearly'))}
                    aria-pressed={billingCycle === 'yearly'}
                    aria-disabled={busy || undefined}
                    className={cx(
                      'ee-modal-seg__option',
                      billingCycle === 'yearly' && 'ee-modal-seg__option--on',
                    )}
                  >
                    Yearly
                    <Pill variant="success">Save ~17%</Pill>
                  </button>
                </div>

                {/* Plan Cards */}
                <div className="ee-modal-plans ee-modal-plans--two">
                  {/* Premium Plan */}
                  {premiumPlan
                    ? (() => {
                        const comparison = getPlanComparison(premiumPlan);
                        const isCurrent = comparison === 'current';
                        return (
                          <WebPlanCard
                            plan={premiumPlan}
                            name="Premium"
                            icon={LuCrown}
                            current={isCurrent}
                            selected={selectedPlan === premiumPlan.stripe_price_id}
                            onClick={() =>
                              !isCurrent && setSelectedPlan(premiumPlan.stripe_price_id)
                            }
                            locked={busy}
                            // Current/Upgrade/Downgrade Badge
                            badge={
                              comparison !== 'upgrade'
                                ? isCurrent
                                  ? 'CURRENT PLAN'
                                  : 'DOWNGRADE'
                                : null
                            }
                            badgeVariant={isCurrent ? 'neutral' : 'gold'}
                            period={billingCycle === 'yearly' ? 'year' : 'month'}
                            savings={
                              billingCycle === 'yearly' && premiumMonthly && !isCurrent
                                ? getYearlySavings(
                                    typeof premiumMonthly.price === 'number'
                                      ? premiumMonthly.price
                                      : parseFloat(premiumMonthly.price),
                                    typeof premiumPlan.price === 'number'
                                      ? premiumPlan.price
                                      : parseFloat(premiumPlan.price),
                                  ).savings
                                : null
                            }
                            included="All Premium features"
                            credits="2,500"
                          />
                        );
                      })()
                    : null}

                  {/* Elite Plan */}
                  {elitePlan
                    ? (() => {
                        const comparison = getPlanComparison(elitePlan);
                        const isCurrent = comparison === 'current';
                        return (
                          <WebPlanCard
                            plan={elitePlan}
                            name="Elite"
                            icon={LuStar}
                            elite
                            current={isCurrent}
                            selected={selectedPlan === elitePlan.stripe_price_id}
                            onClick={() => !isCurrent && setSelectedPlan(elitePlan.stripe_price_id)}
                            locked={busy}
                            // Badge - Current, Downgrade, or Power User
                            badge={
                              isCurrent
                                ? 'CURRENT PLAN'
                                : comparison === 'downgrade'
                                  ? 'DOWNGRADE'
                                  : 'POWER USER'
                            }
                            badgeVariant={
                              isCurrent ? 'neutral' : comparison === 'downgrade' ? 'gold' : 'accent'
                            }
                            period={billingCycle === 'yearly' ? 'year' : 'month'}
                            savings={
                              billingCycle === 'yearly' && eliteMonthly && !isCurrent
                                ? getYearlySavings(
                                    typeof eliteMonthly.price === 'number'
                                      ? eliteMonthly.price
                                      : parseFloat(eliteMonthly.price),
                                    typeof elitePlan.price === 'number'
                                      ? elitePlan.price
                                      : parseFloat(elitePlan.price),
                                  ).savings
                                : null
                            }
                            included="Everything in Premium"
                            credits="30,000"
                            multiplier="12x"
                          />
                        );
                      })()
                    : null}
                </div>

                {/* Features */}
                <div className="ee-modal-features">
                  <h3 className="ee-modal-features__title">
                    What&apos;s included in Premium & Elite:
                  </h3>
                  <div className="ee-modal-features__grid">
                    {PREMIUM_FEATURES.map((feature) => (
                      <div key={feature.title} className="ee-modal-feature">
                        <div className="ee-modal-feature__emoji">{feature.icon}</div>
                        <div className="ee-modal-feature__text">
                          <h4 className="ee-modal-feature__name">{feature.title}</h4>
                          <p className="ee-modal-feature__desc">{feature.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Purchase Button */}
                {(() => {
                  const selectedPlanData = webPlans.find((p) => p.stripe_price_id === selectedPlan);
                  const comparison = selectedPlanData
                    ? getPlanComparison(selectedPlanData)
                    : 'upgrade';
                  const buttonText =
                    currentPlanId === 'free'
                      ? 'Subscribe Now'
                      : comparison === 'upgrade'
                        ? 'Upgrade Now'
                        : 'Change Plan';

                  return (
                    <Button
                      onClick={fireAndForget(handleWebPurchase)}
                      disabled={!selectedPlan}
                      loading={isPurchasingWeb}
                      size="lg"
                      fullWidth
                      leadingIcon={<LuCrown />}
                      trailingIcon={<LuExternalLink />}
                    >
                      {isPurchasingWeb ? 'Redirecting to checkout...' : buttonText}
                    </Button>
                  );
                })()}

                {/* Secure checkout note */}
                <p className="ee-modal__fineprint">
                  Secure checkout powered by Stripe. Subscription automatically renews unless
                  cancelled. You can manage your subscription anytime in your account settings.
                </p>
                {/* Terms of Use and Privacy Links */}
                <div className="ee-modal-legal">
                  <a
                    href={TERMS_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => {
                      if (!isWeb) {
                        e.preventDefault();
                        openLegal(TERMS_URL);
                      }
                    }}
                    className="ee-modal-legal__link"
                  >
                    Terms of Use (EULA)
                  </a>
                  <span>|</span>
                  <a
                    href={PRIVACY_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => {
                      if (!isWeb) {
                        e.preventDefault();
                        openLegal(PRIVACY_URL);
                      }
                    }}
                    className="ee-modal-legal__link"
                  >
                    Privacy Policy
                  </a>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Mobile platform with IAP
  // Get the correct product based on billing cycle
  const getMobileProduct = (tier: string, cycle: string): MobileSubscriptionProduct | undefined => {
    const key = `${tier.toUpperCase()}_${cycle.toUpperCase()}`;
    return SUBSCRIPTION_PRODUCTS[key as keyof typeof SUBSCRIPTION_PRODUCTS];
  };

  const mobilePremiumProduct = getMobileProduct('premium', billingCycle);
  const mobileEliteProduct = getMobileProduct('elite', billingCycle);

  const isMobileCurrentPlan = (
    planId: 'premium' | 'elite',
    cycle: 'monthly' | 'yearly',
  ): boolean => {
    if (!currentSubscription) return false;

    // If cancelled, allow re-subscribe
    if (currentSubscription.status === 'cancelled') return false;

    const currentBilling = normalizeBillingPeriod(currentSubscription.billing_period);

    return currentPlanId === planId && currentBilling === normalizeBillingPeriod(cycle);
  };

  const isPremiumCurrent = isMobileCurrentPlan('premium', billingCycle);
  const isEliteCurrent = isMobileCurrentPlan('elite', billingCycle);

  const selectedProduct = products.find((p) => p.id === selectedPlan);

  const isSelectedCurrent =
    selectedProduct &&
    isMobileCurrentPlan(selectedProduct.plan_id as 'premium' | 'elite', billingCycle);

  return (
    <div
      ref={dialogRef}
      tabIndex={-1}
      className="ee-modal fixed inset-0 z-(--ee-z-dialog)"
      role="dialog"
      aria-modal="true"
      aria-labelledby="subscription-modal-title"
    >
      {/* Modal wrapper */}
      <div className="ee-card ee-modal__dialog">
        {/* Header */}
        <div className="ee-modal__head">
          <div className="ee-modal__heading">
            <IconMedallion icon={LuCrown} size="sm" className="ee-modal__medallion" />
            <div>
              <h2 id="subscription-modal-title" className="ee-modal__title">
                Upgrade Your Plan
              </h2>
              <p className="ee-modal__sub">Unlock all features</p>
            </div>
          </div>

          <IconButton label="Close" icon={<LuX />} {...closeProps} />
        </div>

        {/* Scrollable Content */}
        <div className="ee-modal__body">
          {/* Billing Toggle */}
          <div className="ee-modal-seg">
            <button
              onClick={clickGuard(busy, () => setBillingCycle('monthly'))}
              aria-pressed={billingCycle === 'monthly'}
              aria-disabled={busy || undefined}
              className={cx(
                'ee-modal-seg__option',
                billingCycle === 'monthly' && 'ee-modal-seg__option--on',
              )}
            >
              Monthly
            </button>
            <button
              onClick={clickGuard(busy, () => setBillingCycle('yearly'))}
              aria-pressed={billingCycle === 'yearly'}
              aria-disabled={busy || undefined}
              className={cx(
                'ee-modal-seg__option',
                billingCycle === 'yearly' && 'ee-modal-seg__option--on',
              )}
            >
              Yearly
              <Pill variant="success">Save ~17%</Pill>
            </button>
          </div>

          {/* Plans */}
          <div className="ee-modal-plans">
            <MobilePlanCard
              product={mobilePremiumProduct || null}
              isSelected={isPremiumCurrent || selectedPlan === mobilePremiumProduct?.id}
              disabled={isPremiumCurrent}
              locked={busy}
              onSelect={() => {
                if (!isPremiumCurrent) {
                  setSelectedPlan(mobilePremiumProduct?.id || null);
                }
              }}
              icon={LuCrown}
              accentColor="primary"
              badge={isPremiumCurrent ? 'CURRENT PLAN' : undefined}
              limits={[{ value: formatCredits('premium'), label: 'credits/mo' }]}
              products={products}
            />
            <MobilePlanCard
              product={mobileEliteProduct || null}
              isSelected={isEliteCurrent || selectedPlan === mobileEliteProduct?.id}
              disabled={isEliteCurrent}
              locked={busy}
              onSelect={() => {
                if (!isEliteCurrent) {
                  setSelectedPlan(mobileEliteProduct?.id || null);
                }
              }}
              icon={LuStar}
              accentColor="purple"
              badge={isEliteCurrent ? 'CURRENT PLAN' : 'POWER USER'}
              limits={[
                {
                  value: formatCredits('elite'),
                  label: 'credits/mo',
                  multiplier: `${PLAN_CREDITS.elite / PLAN_CREDITS.premium}x`,
                },
              ]}
              products={products}
            />
          </div>
          {/* Features */}
          <div className="ee-modal-features">
            <h3 className="ee-modal-features__title">What&apos;s included:</h3>
            <div className="ee-modal-features__grid">
              {PREMIUM_FEATURES.map((feature) => (
                <div key={feature.title} className="ee-modal-feature">
                  <div className="ee-modal-feature__emoji">{feature.icon}</div>
                  <div className="ee-modal-feature__text">
                    <h4 className="ee-modal-feature__name">{feature.title}</h4>
                    <p className="ee-modal-feature__desc">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          {/* Purchase Button */}
          <Button
            onClick={fireAndForget(handlePurchase)}
            disabled={!selectedPlan || !!isSelectedCurrent}
            loading={isPurchasing}
            aria-disabled={busy || undefined}
            size="lg"
            fullWidth
            leadingIcon={isSelectedCurrent ? undefined : <LuCrown />}
          >
            {isSelectedCurrent
              ? 'Already Subscribed'
              : isPurchasing
                ? 'Processing...'
                : 'Subscribe Now'}
          </Button>

          <Button
            onClick={fireAndForget(handleRestore)}
            loading={isRestoring}
            aria-disabled={busy || undefined}
            variant="ghost"
            fullWidth
            className="ee-modal__restore"
            leadingIcon={<LuRefreshCw />}
          >
            Restore Purchases
          </Button>

          {/* Terms */}
          <p className="ee-modal__fineprint">
            Subscription automatically renews unless cancelled at least 24 hours before the end of
            the current period.
          </p>
          {/* Legal Links */}
          <div className="ee-modal-legal">
            <a
              href={TERMS_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => {
                if (!isWeb) {
                  e.preventDefault();
                  openLegal(TERMS_URL);
                }
              }}
              className="ee-modal-legal__link"
            >
              Terms of Use (EULA)
            </a>
            <span>|</span>
            <a
              href={PRIVACY_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => {
                if (!isWeb) {
                  e.preventDefault();
                  openLegal(PRIVACY_URL);
                }
              }}
              className="ee-modal-legal__link"
            >
              Privacy Policy
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionModal;
